import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import type { PromoCarouselLocation, PublicBoost } from '~/domains/promotions/types';
import type {
  BonusDefinitionResponse,
  VoucherDefinitionResponse,
} from '~/domains/promotions/types/promo-code.types';

export interface MissionCardProps {
  missionAssignment: MissionAssignment;
  hasBorder?: boolean;
  isCompact?: boolean;
  onClick?: () => void;
}

/**
 * Mission lifecycle status — must match backend gamification-service.
 * NOTE: `available` is intentionally absent; the API rejects it with HTTP 400.
 * `started` = active view; `completed` / `expired` = history views.
 */
// eslint-disable-next-line no-restricted-syntax
export enum MissionStatus {
  STARTED = 'started',
  COMPLETED = 'completed',
  EXPIRED = 'expired',
}

/**
 * Mission source type — must match backend enum.
 * @todo TBD by @chase (EDGE-28825) — confirm before relying on these literals.
 */
export type MissionType = 'QUICKPICKS' | 'CONTEST';

/** Present only when status === 'started'; `null` on completed/expired rows. */
export interface MissionProgress {
  current: number;
  required: number;
}

/**
 * Reward bundle — returned ONLY on the mission detail response (not the list).
 * Exactly the promoCodes reward schema, so `parseRewardsToPromoTickets` consumes
 * it directly.
 */
export interface MissionRewards {
  boosts: PublicBoost[];
  vouchers: VoucherDefinitionResponse[];
  bonuses: BonusDefinitionResponse[];
}

export interface MissionAssignment {
  /** Prefixed ULID (e.g. "miassgn_01J…"); pass back verbatim to the detail endpoint. */
  assignmentId: string;
  mission: Mission;
  status: MissionStatus;
  /** `null` on completed/expired; present when status === 'started'. */
  progress: MissionProgress | null;
  completedAt: string | null;
  /**
   * Whether the completion modal has already been shown for this assignment
   * (server-tracked, cross-device). ISO datetime, or `null` = not yet seen.
   * Only meaningful when `status === 'completed'`.
   */
  completionSeenAt: string | null;
  /**
   * Soft ordering hint (epoch-ms) mirroring the Pusher `mission_progress` payload.
   * Used only to avoid rendering an older snapshot over a newer one; never a reason
   * to skip a refetch. Optional for back-compat with older responses.
   */
  rev?: number;
}
export interface MissionDetailAssignment extends MissionAssignment {
  mission: MissionDetail;
}

export interface Mission {
  slug: string;
  title: string;
  type: MissionType;
  /** Value displayed in the hexagon badge (generic variant). */
  rewardValue: string;
  description: string;
  terms: string;
  /** May be "" when not configured. */
  rewardSummary: string;
  /** Badge icon; when present the icon variant overrides `rewardValue`. */
  iconUrl: string | null;
  /** ISO-8601 datetime; `null` = evergreen (no end date). */
  endsAt: string | null;
}
export interface MissionDetail extends Mission {
  /** Detail endpoint only — omitted from the list response. */
  rewards?: MissionRewards;
}

export type MissionsResponse = PaginatedData<MissionAssignment>;
export type MissionDetailResponse = MissionDetailAssignment;

export interface GetMissionsParams {
  status?: MissionStatus;
  offset?: number;
  limit?: number;
  location: PromoCarouselLocation;
}
